import { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronRight, Upload, Clipboard, Sparkles, Loader2, X, Plus, Eye, Check, Maximize2, Wand2, ImageIcon, MoreVertical, Merge, Scissors, Type, Play } from 'lucide-react';
import {
  Scene, SceneType, OverlayTemplate, EffectType, SoundType,
  BrollSourceType, InfographicDirection, InfographicBgStyle, ComponentType, CaptionStyle, CaptionPosition, PipPosition, CaptionPositionNum,
  OverlayLibraryEntry, BUILTIN_OVERLAYS,
  SCENE_TYPE_CONFIG, EFFECTS, SOUNDS,
  BROLL_SOURCES, INFOGRAPHIC_DIRECTIONS, INFOGRAPHIC_BG_STYLES, COMPONENT_TYPES, CAPTION_STYLES, CAPTION_POSITIONS, PIP_POSITIONS
} from '../types';
import { loadSettings, AppSettings, CaptionPreset, CaptionSettings, POSITION_LABELS, EDGE_STYLES } from './SettingsPanel';
import { ScenePreview } from './ScenePreview';

type SceneStatus = 'done' | 'empty' | 'preview' | 'speaker-only';

interface SceneAccordionProps {
  scene: Scene;
  isExpanded: boolean;
  onToggle: () => void;
  onUpdate: (scene: Scene) => void;
  onSeek?: () => void;
  status?: SceneStatus;
  onMergeNext?: () => void;
  onSplit?: (splitWordIndex: number) => void;
  isLastScene?: boolean;
  avatarUrl?: string;
}

const FAL_API_KEY = '1c0e869c-2d2a-4f99-96cc-a4c032623b70:43452dfb9d146eb90e88da6d34e06809';
const PEXELS_API_KEY = 'eEXwC5tOP1nizVIdUquIwmkXRLk75BSl5SfEfds5BHZPsdHoTjrajFfz';
const S3_WORKER = 'https://scene-editor-s3.daniel-807.workers.dev';

// Module-level overlay library cache — fetched once, shared across all SceneAccordion instances
let _overlayLibrary: OverlayLibraryEntry[] | null = null;
let _overlayFetchPromise: Promise<OverlayLibraryEntry[]> | null = null;

function fetchOverlayLibrary(): Promise<OverlayLibraryEntry[]> {
  if (_overlayLibrary) return Promise.resolve(_overlayLibrary);
  if (_overlayFetchPromise) return _overlayFetchPromise;
  _overlayFetchPromise = fetch(`${S3_WORKER}/list-overlays`)
    .then(r => r.json())
    .then((data: any) => {
      _overlayLibrary = data.overlays || BUILTIN_OVERLAYS;
      return _overlayLibrary!;
    })
    .catch(() => {
      _overlayLibrary = BUILTIN_OVERLAYS;
      return _overlayLibrary;
    });
  return _overlayFetchPromise;
}

// Safe array helper - ensures we always get an array even if input is object/undefined/null
const ensureArray = <T,>(value: T[] | unknown): T[] => Array.isArray(value) ? value : [];

async function uploadToS3(imageUrl: string): Promise<string> {
  // Already on S3 or external host - return as-is
  if (imageUrl.includes('s3.') || imageUrl.includes('amazonaws.com') || imageUrl.includes('catbox.moe') || imageUrl.includes('cloudfront.net')) {
    return imageUrl;
  }
  
  if (imageUrl.startsWith('data:')) {
    const response = await fetch(`${S3_WORKER}/upload-image`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image: imageUrl }),
    });
    const data = await response.json();
    if (data.error) throw new Error(data.error);
    return data.url;
  }
  
  // Convert URL to data URL then upload
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const reader = new FileReader();
    const dataUrl = await new Promise<string>((resolve) => {
      reader.onload = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
    return uploadToS3(dataUrl);
  } catch {
    return imageUrl;
  }
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// Get CSS position for PIP based on grid position (3x3)
function getPipStyle(position: PipPosition = 'bottom-right', size: 'full' | 'mini' = 'full'): React.CSSProperties {
  const pipSize = size === 'full' ? 56 : 24; // px
  const margin = size === 'full' ? 16 : 4; // px
  
  const base: React.CSSProperties = {
    position: 'absolute',
    width: pipSize,
    height: pipSize,
    borderRadius: '50%',
    overflow: 'hidden',
    border: size === 'full' ? '2px solid white' : '1px solid white',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  };
  
  switch (position) {
    case 'top-left': return { ...base, top: margin, left: margin };
    case 'top-center': return { ...base, top: margin, left: '50%', transform: 'translateX(-50%)' };
    case 'top-right': return { ...base, top: margin, right: margin };
    case 'middle-left': return { ...base, top: '50%', left: margin, transform: 'translateY(-50%)' };
    case 'middle-center': return { ...base, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    case 'middle-right': return { ...base, top: '50%', right: margin, transform: 'translateY(-50%)' };
    case 'bottom-left': return { ...base, bottom: margin * 2, left: margin };
    case 'bottom-center': return { ...base, bottom: margin * 2, left: '50%', transform: 'translateX(-50%)' };
    case 'bottom-right': 
    default: return { ...base, bottom: margin * 2, right: margin };
  }
}

// B-roll source state uses BrollSourceType from types

export function SceneAccordion({ scene, isExpanded, onToggle, onUpdate, onSeek, status = 'empty', onMergeNext, onSplit, isLastScene = false, avatarUrl }: SceneAccordionProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [prompt, setPrompt] = useState(scene.broll?.prompt || '');
  const [selectedAssets, setSelectedAssets] = useState<string[]>(scene.broll?.url ? [scene.broll.url] : []);
  const [brollSource, setBrollSource] = useState<BrollSourceType>(scene.broll?.source || 'flux');
  const [componentNotes, setComponentNotes] = useState(scene.broll?.componentNotes || '');
  const [searchResults, setSearchResults] = useState<string[]>([]);
  const [previewingUrl, setPreviewingUrl] = useState<string | null>(null);
  const [appSettings, setAppSettings] = useState<AppSettings | null>(null);
  const [libraryAssets, setLibraryAssets] = useState<{url: string; filename: string; category: string; mediaType?: 'image' | 'video'; thumbnailUrl?: string; duration?: number}[]>([]);
  const [libraryCategory, setLibraryCategory] = useState<string>('all');
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [overlayLibrary, setOverlayLibrary] = useState<OverlayLibraryEntry[]>(_overlayLibrary || BUILTIN_OVERLAYS);

  // Fetch overlay library on mount
  useEffect(() => {
    fetchOverlayLibrary().then(setOverlayLibrary);
  }, []);

  // Load app settings (for presets) - refresh when expanded
  useEffect(() => {
    loadSettings().then(setAppSettings);
  }, [isExpanded]);
  
  // Fetch library assets when library source is selected
  useEffect(() => {
    if (brollSource === 'library' && ensureArray(libraryAssets).length === 0) {
      setLoadingLibrary(true);

      const METADATA_URL = 'https://remotionlambda-useast1-2eywyfynbf.s3.us-east-1.amazonaws.com/assets/library/metadata.json';

      Promise.all([
        fetch(`${S3_WORKER}/list-assets?category=${libraryCategory}`).then(r => r.json()).catch(() => ({ assets: {} })),
        fetch(METADATA_URL).then(r => r.ok ? r.json() : { clips: [] }).catch(() => ({ clips: [] })),
      ]).then(([workerData, metadata]) => {
        // Flatten worker response (object with category keys → flat array)
        const workerAssets: {url: string; filename: string; category: string; mediaType?: 'image' | 'video'}[] = [];
        const assetObj = workerData.assets || {};
        for (const [cat, items] of Object.entries(assetObj)) {
          if (Array.isArray(items)) {
            for (const item of items as {key?: string; url: string; name?: string}[]) {
              const isVideo = /\.(mp4|webm|mov)$/i.test(item.url);
              workerAssets.push({
                url: item.url,
                filename: item.name || item.key?.split('/').pop() || 'asset',
                category: cat,
                mediaType: isVideo ? 'video' : 'image',
              });
            }
          }
        }

        // Add clips from metadata JSON
        const clipAssets = ensureArray(metadata.clips).map((clip: Record<string, unknown>) => ({
          url: clip.url as string,
          filename: clip.filename as string || clip.id as string || 'clip',
          category: 'clips',
          mediaType: (clip.mediaType as 'image' | 'video') || 'video',
          thumbnailUrl: clip.thumbnailUrl as string | undefined,
          duration: clip.duration as number | undefined,
        }));

        // Merge (clips first, then worker assets)
        const all = [...clipAssets, ...workerAssets];

        // Filter by category
        const filtered = libraryCategory === 'all' ? all : all.filter(a => a.category === libraryCategory);
        setLibraryAssets(filtered);
        setLoadingLibrary(false);
      }).catch(() => setLoadingLibrary(false));
    }
  }, [brollSource, libraryCategory]);
  
  // Get caption settings for this scene
  const getCaptionSettings = () => {
    if (!appSettings) return null;
    if (scene.captionPreset && scene.captionPreset !== 'default') {
      const preset = appSettings.captionPresets?.find(p => p.id === scene.captionPreset);
      if (preset) return preset.settings;
    }
    return appSettings.captions;
  };
  
  const captionSettings = getCaptionSettings();
  const captionPosition = scene.captionPositionOverride || captionSettings?.position || 7;
  
  // Caption overlay component
  const CaptionOverlay = ({ size = 'full' }: { size?: 'full' | 'mini' }) => {
    if (!captionSettings) return null;
    const words = scene.transcript.split(' ').slice(0, captionSettings.wordsShown || 2).join(' ');
    const posPercent = ((captionPosition - 1) / 8) * 85 + 5; // Map 1-9 to 5%-90% (5=center at 50%)
    const fontSize = size === 'mini' ? 6 : (captionSettings.fontSize || 32) * 0.4;
    const padding = size === 'mini' ? 2 : (captionSettings.padding || 16) * 0.4;
    const edgeRadius = EDGE_STYLES.find(e => e.id === captionSettings.edgeStyle)?.radius || 9999;
    
    return (
      <div 
        className="absolute left-1/2 -translate-x-1/2 z-10"
        style={{ top: `${posPercent}%` }}
      >
        <div
          style={{
            backgroundColor: captionSettings.backgroundColor || '#34A49F',
            color: captionSettings.textColor || '#FFFFFF',
            fontFamily: `'${captionSettings.fontFamily || 'Poppins'}', sans-serif`,
            fontWeight: captionSettings.fontWeight || 600,
            fontSize: `${fontSize}px`,
            padding: `${padding * 0.5}px ${padding}px`,
            borderRadius: edgeRadius === 9999 ? '9999px' : `${edgeRadius * 0.4}px`,
            textTransform: captionSettings.textCase === 'none' ? 'none' : captionSettings.textCase || 'capitalize',
            whiteSpace: 'nowrap',
          }}
        >
          {words || 'Caption'}
        </div>
      </div>
    );
  };
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showSplitDialog, setShowSplitDialog] = useState(false);
  const [splitAtWord, setSplitAtWord] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'preview' | 'controls'>('controls');
  const [previewSize, setPreviewSize] = useState<'S' | 'M' | 'L'>('M');
  const [animationKey, setAnimationKey] = useState(0); // Triggers animation replay
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Sound URLs for preview - using S3 hosted files
  const SWOOSH_URL = 'https://remotionlambda-useast1-2eywyfynbf.s3.us-east-1.amazonaws.com/assets/swoosh.wav';
  const SOUND_URLS: Record<string, string> = {
    'swoosh': SWOOSH_URL,
    'pop': SWOOSH_URL, // TODO: add unique sounds
    'whoosh': SWOOSH_URL,
    'click': SWOOSH_URL,
    'rise': SWOOSH_URL,
  };

  // Play sound effect
  const playSound = (soundId: string) => {
    if (soundId && soundId !== 'none' && SOUND_URLS[soundId]) {
      const audio = new Audio(SOUND_URLS[soundId]);
      audio.volume = 0.7;
      audio.play().catch(() => {}); // Ignore autoplay errors
    }
  };

  // Replay animation when effect changes (and play sound)
  const replayAnimation = () => {
    setAnimationKey(k => k + 1);
    if (scene.sound && scene.sound !== 'none') {
      playSound(scene.sound);
    }
  };
  
  const config = SCENE_TYPE_CONFIG[scene.type] || SCENE_TYPE_CONFIG.SPEAKER_ONLY;
  
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });
      
      // Upload to S3 immediately
      const hostedUrl = await uploadToS3(dataUrl);
      
      // Update scene with the uploaded URL
      setSelectedAssets(prev => [...prev, hostedUrl]);
      onUpdate({ ...scene, broll: { prompt: file.name, source: 'upload', url: hostedUrl } });
      
      // Also add to search results for preview
      setSearchResults(prev => [hostedUrl, ...prev]);
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleFileUpload(file);
    }
  };

  const handleTypeChange = (newType: SceneType) => {
    const newConfig = SCENE_TYPE_CONFIG[newType] || SCENE_TYPE_CONFIG.SPEAKER_ONLY;
    const templateId = scene.overlayTemplate || 'phone';
    const entry = overlayLibrary.find(o => o.id === templateId);
    onUpdate({
      ...scene,
      type: newType,
      broll: newConfig.brollRequired ? scene.broll || { prompt: '', source: 'generated' } : null,
      overlayTemplate: newType === 'OVERLAY' ? templateId : undefined,
      overlay: newType === 'OVERLAY' ? {
        template: templateId,
        pngUrl: entry?.pngUrl,
        cutout: entry?.cutout,
        objectPosition: entry?.objectPosition,
      } : undefined,
    });
  };

  const handleOverlayTemplateChange = (templateId: string) => {
    const entry = overlayLibrary.find(o => o.id === templateId);
    onUpdate({
      ...scene,
      overlayTemplate: templateId as OverlayTemplate,
      overlay: {
        template: templateId,
        pngUrl: entry?.pngUrl,
        cutout: entry?.cutout,
        objectPosition: entry?.objectPosition,
      },
    });
  };

  const searchBraveImages = async () => {
    if (!prompt) return;
    setIsGenerating(true);
    setSearchResults([]);
    
    try {
      const response = await fetch(`https://brave-image-proxy.daniel-807.workers.dev/?q=${encodeURIComponent(prompt)}`);
      const data = await response.json();
      if (data.images?.length > 0) {
        const imageUrls = data.images.map((img: any) => img.thumbnail || img.full).filter(Boolean);
        setSearchResults(imageUrls);
      }
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const searchPexels = async () => {
    if (!prompt) return;
    setIsGenerating(true);
    setSearchResults([]);

    try {
      // Search both photos and videos in parallel
      const [photosRes, videosRes] = await Promise.all([
        fetch(`https://api.pexels.com/v1/search?query=${encodeURIComponent(prompt)}&per_page=12&orientation=portrait`, {
          headers: { 'Authorization': PEXELS_API_KEY },
        }),
        fetch(`https://api.pexels.com/videos/search?query=${encodeURIComponent(prompt)}&per_page=6&orientation=portrait`, {
          headers: { 'Authorization': PEXELS_API_KEY },
        }),
      ]);

      const photosData = await photosRes.json();
      const videosData = await videosRes.json();

      const photoUrls = (photosData.photos || []).map((p: any) => p.src.large2x || p.src.large);
      // For videos, pick the HD file and use the image as thumbnail for the grid
      const videoUrls = (videosData.videos || []).map((v: any) => {
        const hdFile = v.video_files.find((f: any) => f.quality === 'hd' && f.width >= 720) || v.video_files[0];
        return hdFile?.link;
      }).filter(Boolean);

      // Show photos first, then videos
      setSearchResults([...photoUrls, ...videoUrls]);
    } catch (err) {
      console.error('Pexels search failed:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const generateFluxImage = async () => {
    if (!prompt) return;
    setIsGenerating(true);
    
    try {
      const imageSize = config.brollShape === 'square' ? 'square_hd' : 'portrait_16_9';
      const submitResponse = await fetch('https://queue.fal.run/fal-ai/flux/dev', {
        method: 'POST',
        headers: { 'Authorization': `Key ${FAL_API_KEY}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: `${prompt}, cinematic, high quality, professional`, image_size: imageSize, num_images: 1 }),
      });
      
      const queueData = await submitResponse.json();
      let result = null;
      
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 2000));
        const statusResponse = await fetch(queueData.response_url, { headers: { 'Authorization': `Key ${FAL_API_KEY}` } });
        const statusData = await statusResponse.json();
        if (statusData.images?.[0]) { result = statusData.images[0].url; break; }
      }
      
      if (result) {
        setSearchResults(prev => [result, ...prev]);
        setPreviewingUrl(result);
      }
    } catch (err) {
      console.error('Generation failed:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateOrSearch = () => {
    if (brollSource === 'brave') searchBraveImages();
    else if (brollSource === 'pexels') searchPexels();
    else if (brollSource === 'flux' || brollSource === 'lora') generateFluxImage();
  };

  const addSearchResultToAssets = async (url: string) => {
    setIsUploading(true);
    try {
      const hostedUrl = await uploadToS3(url);
      setSelectedAssets(prev => [...prev, hostedUrl]);
      onUpdate({ ...scene, broll: { prompt, source: 'url', url: hostedUrl } });
    } catch (err) {
      setSelectedAssets(prev => [...prev, url]);
      onUpdate({ ...scene, broll: { prompt, source: 'url', url } });
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) handleFileUpload(file);
    };
    input.click();
  };

  const handlePaste = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        for (const type of item.types) {
          if (type.startsWith('image/')) {
            const blob = await item.getType(type);
            const reader = new FileReader();
            reader.onload = () => {
              const url = reader.result as string;
              setSearchResults(prev => [url, ...prev]);
              setPreviewingUrl(url);
            };
            reader.readAsDataURL(blob);
            return;
          }
        }
      }
    } catch (err) {
      console.error('Paste failed:', err);
    }
  };

  const removeAsset = (index: number) => {
    const newAssets = selectedAssets.filter((_, i) => i !== index);
    setSelectedAssets(newAssets);
    onUpdate({ ...scene, broll: newAssets.length > 0 ? { ...scene.broll!, url: newAssets[0] } : scene.broll ? { ...scene.broll, url: undefined } : null });
  };

  const selectAsset = (url: string) => {
    onUpdate({ ...scene, broll: { prompt: scene.broll?.prompt || prompt, source: 'generated', url } });
  };

  // Auto-status: green if complete, red if missing required b-roll
  const isComplete = !config.brollRequired ||
    scene.broll?.source === 'component' ||
    !!(scene.broll?.url && scene.broll.url.startsWith('http'));

  const getHeaderStyles = () => {
    if (isComplete) return 'bg-green-50 border-green-200 hover:bg-green-100';
    return 'bg-red-50 border-red-200 hover:bg-red-100';
  };

  const getTextStyles = () => {
    if (isComplete) return 'text-green-900';
    return 'text-red-900';
  };

  return (
    <div className={`rounded-lg border overflow-hidden transition-all ${getHeaderStyles()}`}>
      {/* Header Row */}
      <div className="flex items-center px-4 py-3 gap-3">
        <button onClick={onToggle} className="flex items-center gap-3 flex-1 min-w-0 text-left">
          {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />}
          
          <div className="flex-1 min-w-0">
            <p className={`text-xs leading-relaxed ${getTextStyles()}`}>
              {showSplitDialog ? (
                scene.transcript.split(' ').map((word, i, arr) => (
                  <span
                    key={i}
                    onClick={(e) => { e.stopPropagation(); setSplitAtWord(i + 1); }}
                    className={`cursor-text hover:bg-yellow-200 rounded transition-colors ${splitAtWord !== null && i < splitAtWord ? 'bg-green-200' : ''}${splitAtWord !== null && i >= splitAtWord ? 'bg-blue-200' : ''}`}
                  >
                    {word}{i < arr.length - 1 ? ' ' : ''}
                  </span>
                ))
              ) : scene.transcript}
            </p>
            <p className="text-xs text-gray-500 mt-1">{formatTime(scene.start)} – {formatTime(scene.end)} • {config.label}</p>
          </div>
        </button>
        
        {/* Status icon */}
        <div className="flex-shrink-0">
          {status === 'speaker-only' ? (
            <Wand2 className="w-5 h-5 text-gray-400" />
          ) : status === 'done' && scene.broll?.url ? (
            <div className="w-8 h-8 rounded overflow-hidden border border-green-300 bg-gray-100">
              <img src={scene.broll.url} alt="" className="w-full h-full object-cover" />
            </div>
          ) : (
            <ImageIcon className={`w-5 h-5 ${status === 'empty' ? 'text-red-400' : 'text-yellow-500'}`} />
          )}
        </div>
        
        {/* Kebab menu */}
        <div className="relative flex-shrink-0" ref={menuRef}>
          <button onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }} className="p-1.5 hover:bg-black/10 rounded transition-colors">
            <MoreVertical className="w-4 h-4 text-gray-500" />
          </button>

          {showMenu && (
            <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[160px]">
              {!isLastScene && onMergeNext && (
                <button onClick={() => { onMergeNext(); setShowMenu(false); }} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2">
                  <Merge className="w-4 h-4" /> Merge with next
                </button>
              )}
              {onSplit && (
                <button onClick={() => { setShowSplitDialog(true); setShowMenu(false); }} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2">
                  <Scissors className="w-4 h-4" /> Split scene
                </button>
              )}
            </div>
          )}
        </div>

        {/* Status dot — auto-calculated, no manual approval needed */}
        <div className={`flex-shrink-0 w-3 h-3 rounded-full ${isComplete ? 'bg-green-500' : 'bg-red-400'}`} />
      </div>
      
      {/* Split Controls */}
      {showSplitDialog && (
        <div className="px-4 py-2 bg-yellow-50 border-t border-yellow-200 flex items-center gap-2">
          <span className="text-xs text-yellow-700">Click text above to split</span>
          <div className="flex-1" />
          {splitAtWord !== null && (
            <button onClick={() => { onSplit?.(splitAtWord); setShowSplitDialog(false); setSplitAtWord(null); }} className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700">Split Here</button>
          )}
          <button onClick={() => { setShowSplitDialog(false); setSplitAtWord(null); }} className="px-3 py-1 text-gray-600 text-xs hover:bg-gray-100 rounded">Cancel</button>
        </div>
      )}

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-100 bg-white">
          {/* Notes — always visible, for any feedback */}
          <div className="px-3 pt-3">
            <textarea
              value={scene.notes || ''}
              onChange={(e) => onUpdate({ ...scene, notes: e.target.value || undefined })}
              placeholder="Add notes... (e.g., 'swap to a list', 'use a different image', 'make shorter')"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm h-10 resize-none focus:h-20 focus:border-blue-300 transition-all placeholder:text-gray-400"
            />
          </div>

          {/* Mobile: Tab switcher */}
          <div className="flex md:hidden border-b border-gray-200">
            <button onClick={() => setActiveTab('preview')} className={`flex-1 py-2 text-xs font-medium transition-colors ${activeTab === 'preview' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500'}`}>Preview</button>
            <button onClick={() => setActiveTab('controls')} className={`flex-1 py-2 text-xs font-medium transition-colors ${activeTab === 'controls' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500'}`}>Controls</button>
          </div>
          
          <div className="p-3">
            {/* Mobile: Tab content */}
            <div className="md:hidden">
              {activeTab === 'preview' && (
                <ScenePreview
                  scene={scene}
                  avatarUrl={avatarUrl}
                  previewingUrl={previewingUrl}
                  captionSettings={captionSettings}
                  onReplay={scene.effect && scene.effect !== 'none' ? replayAnimation : undefined}
                  className="max-h-[50vh] rounded-lg border border-gray-200"
                />
              )}
              {activeTab === 'controls' && (
                <div className="space-y-3">
                  {/* Top section: Controls left, Scene preview right */}
                  <div className="flex gap-3">
                    {/* Left column: first controls */}
                    <div className="flex-1 space-y-2">
                      <div>
                        <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Scene Type</label>
                        <select value={scene.type} onChange={(e) => handleTypeChange(e.target.value as SceneType)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                          {Object.entries(SCENE_TYPE_CONFIG).map(([type, cfg]) => <option key={type} value={type}>{cfg.icon} {cfg.label}</option>)}
                        </select>
                      </div>
                      
                      {/* Effect & Sound */}
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Animation</label>
                          <select 
                            value={scene.effect || 'none'} 
                            onChange={(e) => onUpdate({ ...scene, effect: e.target.value as EffectType })}
                            className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm"
                          >
                            {EFFECTS.map(eff => <option key={eff.id} value={eff.id}>{eff.name}</option>)}
                          </select>
                        </div>
                        <div className="flex-1">
                          <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Sound</label>
                          <select 
                            value={scene.sound || 'none'} 
                            onChange={(e) => {
                              const newSound = e.target.value as SoundType;
                              onUpdate({ ...scene, sound: newSound });
                              playSound(newSound);
                            }}
                            className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm"
                          >
                            {SOUNDS.map(snd => <option key={snd.id} value={snd.id}>{snd.name}</option>)}
                          </select>
                        </div>
                      </div>
                      
                      {/* Caption Controls - Mobile */}
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Caption</label>
                          <select 
                            value={scene.captionPreset || 'default'} 
                            onChange={(e) => onUpdate({ ...scene, captionPreset: e.target.value })}
                            className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm"
                          >
                            <option value="default">Default</option>
                            {appSettings?.captionPresets?.map(preset => (
                              <option key={preset.id} value={preset.id}>{preset.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex-1">
                          <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Position</label>
                          <select 
                            value={scene.captionPositionOverride || ''} 
                            onChange={(e) => onUpdate({ ...scene, captionPositionOverride: e.target.value ? parseInt(e.target.value) as CaptionPositionNum : undefined })}
                            className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm"
                          >
                            <option value="">Default</option>
                            {POSITION_LABELS.map(pos => (
                              <option key={pos.value} value={pos.value}>{pos.value}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Browse/Paste */}
                      {config.brollRequired && (
                        <div className="flex gap-2">
                          <button onClick={handleUpload} className="flex-1 py-2 border rounded-lg text-sm">📁 Browse</button>
                          <button onClick={handlePaste} className="flex-1 py-2 border rounded-lg text-sm">📋 Paste</button>
                        </div>
                      )}
                    </div>
                    
                    {/* Right column: Scene composition preview (mini version of Preview tab) */}
                    <div 
                      className="w-[25%] flex-shrink-0 cursor-pointer"
                      onClick={() => setActiveTab('preview')}
                    >
                      <div className="aspect-[9/16] bg-gray-900 rounded-lg overflow-hidden border border-gray-300 relative">
                        {scene.type === 'SPEAKER_ONLY' && (
                          <>
                            <div className="absolute inset-0 bg-black" />
                            {avatarUrl ? (
                              <video 
                                key={animationKey}
                                src={avatarUrl} 
                                className={`absolute inset-0 w-full h-full object-cover ${scene.effect && scene.effect !== 'none' ? `effect-${scene.effect}` : ''}`} 
                                style={{ objectPosition: 'center 15%' }} 
                                muted 
                                playsInline 
                              />
                            ) : <div className="w-full h-full bg-gray-300 flex items-center justify-center text-2xl">🎤</div>}
                          </>
                        )}
                        {scene.type === 'SPLIT_SCREEN' && (
                          <>
                            <div className="absolute top-0 left-0 right-0 h-1/2 bg-gray-800 flex items-center justify-center overflow-hidden">
                              {(previewingUrl || scene.broll?.url) ? (
                                <img src={previewingUrl || scene.broll?.url} alt="" className="w-full h-full object-cover" />
                              ) : <div className="text-sm">🖼️</div>}
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gray-700 overflow-hidden">
                              {avatarUrl ? (
                                <video src={avatarUrl} className="w-full h-full object-cover" style={{ objectPosition: 'center center' }} muted playsInline />
                              ) : <div className="w-full h-full flex items-center justify-center text-sm">👤</div>}
                            </div>
                          </>
                        )}
                        {scene.type === 'CIRCLE_PIP' && (
                          <>
                            <div className="absolute inset-0 bg-gray-800 flex items-center justify-center overflow-hidden">
                              {(previewingUrl || scene.broll?.url) ? (
                                <img src={previewingUrl || scene.broll?.url} alt="" className="w-full h-full object-cover" />
                              ) : <div className="text-xl">🌄</div>}
                            </div>
                            <div style={getPipStyle(scene.pipPosition, 'mini')}>
                              {avatarUrl ? (
                                <video src={avatarUrl} className="w-full h-full object-cover" style={{ objectPosition: 'center 20%' }} muted playsInline />
                              ) : <div className="w-full h-full bg-gray-300 flex items-center justify-center text-[8px]">👤</div>}
                            </div>
                          </>
                        )}
                        {scene.type === 'FULL_BLEED_BROLL' && (
                          <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
                            {(previewingUrl || scene.broll?.url) ? (
                              <img src={previewingUrl || scene.broll?.url} alt="" className="w-full h-full object-cover" />
                            ) : <div className="text-xl">🌄</div>}
                          </div>
                        )}
                        {scene.type === 'OVERLAY' && scene.overlayTemplate && (
                          <img src={scene.overlay?.pngUrl || overlayLibrary.find(o => o.id === scene.overlayTemplate)?.pngUrl || ''} alt="" className="w-full h-full object-contain" />
                        )}
                        {/* Caption Overlay - Mini Preview */}
                        <CaptionOverlay size="mini" />
                        <div className="absolute bottom-0.5 right-0.5 px-1 py-0.5 bg-black/50 rounded text-[8px] text-white">
                          tap
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {scene.type === 'OVERLAY' && (
                    <select value={scene.overlayTemplate || 'phone'} onChange={(e) => handleOverlayTemplateChange(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                      {overlayLibrary.map(o => <option key={o.id} value={o.id}>📺 {o.name}</option>)}
                    </select>
                  )}

                  {/* PIP Position Picker for CIRCLE_PIP */}
                  {scene.type === 'CIRCLE_PIP' && (
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">PIP Position</label>
                      <div className="grid grid-cols-3 gap-1 w-full max-w-[160px]">
                        {PIP_POSITIONS.map((pos) => (
                          <button
                            key={pos.id}
                            onClick={() => onUpdate({ ...scene, pipPosition: pos.id })}
                            className={`aspect-square rounded border-2 text-xs font-medium transition-all flex items-center justify-center ${
                              (scene.pipPosition || 'bottom-right') === pos.id 
                                ? 'border-green-500 bg-green-50 text-green-700' 
                                : 'border-gray-200 hover:border-gray-300 text-gray-500'
                            }`}
                          >
                            {pos.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {config.brollRequired && (
                    <>
                      {/* B-roll Source Selector */}
                      <select value={brollSource} onChange={(e) => setBrollSource(e.target.value as BrollSourceType)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                        {BROLL_SOURCES.map(src => <option key={src.id} value={src.id}>{src.icon} {src.name}</option>)}
                      </select>
                      
                      {/* Flux/LoRA/Brave - prompt + generate */}
                      {(brollSource === 'flux' || brollSource === 'lora' || brollSource === 'brave' || brollSource === 'pexels') && (
                        <>
                          <input type="text" value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={brollSource === 'brave' || brollSource === 'pexels' ? 'Search query...' : 'Generation prompt...'} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                          <button onClick={handleGenerateOrSearch} disabled={isGenerating || !prompt} className="w-full py-2 bg-green-600 text-white rounded-lg text-sm disabled:opacity-50">
                            {isGenerating ? '...' : brollSource === 'brave' || brollSource === 'pexels' ? '🔍 Search' : '✨ Generate'}
                          </button>
                        </>
                      )}
                      
                      {/* Infographic - upload + options */}
                      {brollSource === 'infographic' && (
                        <>
                          <div className="flex gap-2">
                            <button onClick={handleUpload} className="flex-1 py-2 border rounded-lg text-sm">📁 Upload PNG</button>
                            <button onClick={handlePaste} className="flex-1 py-2 border rounded-lg text-sm">📋 Paste</button>
                          </div>
                          <div className="flex gap-2">
                            <select value={scene.broll?.infographicDirection || 'bottom'} onChange={(e) => onUpdate({ ...scene, broll: { ...scene.broll!, infographicDirection: e.target.value as InfographicDirection } })} className="flex-1 px-2 py-2 border rounded-lg text-sm">
                              {INFOGRAPHIC_DIRECTIONS.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                            <select value={scene.broll?.infographicBgStyle || 'bokeh'} onChange={(e) => onUpdate({ ...scene, broll: { ...scene.broll!, infographicBgStyle: e.target.value as InfographicBgStyle } })} className="flex-1 px-2 py-2 border rounded-lg text-sm">
                              {INFOGRAPHIC_BG_STYLES.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                          </div>
                        </>
                      )}
                      
                      {/* Component - type + notes */}
                      {brollSource === 'component' && (
                        <>
                          <select value={scene.broll?.componentType || 'metric-card'} onChange={(e) => onUpdate({ ...scene, broll: { ...scene.broll!, source: 'component', componentType: e.target.value as ComponentType } })} className="w-full px-3 py-2 border rounded-lg text-sm">
                            {COMPONENT_TYPES.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                          </select>
                          <textarea 
                            value={componentNotes} 
                            onChange={(e) => { setComponentNotes(e.target.value); onUpdate({ ...scene, broll: { ...scene.broll!, componentNotes: e.target.value } }); }}
                            placeholder="Describe what you want (e.g., 'Show 47% engagement increase with green arrow')"
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm h-20 resize-none"
                          />
                        </>
                      )}
                      
                      {/* Lottie - paste URL from LottieFiles */}
                      {brollSource === 'lottie' && (
                        <>
                          <input type="text" value={scene.broll?.url || ''} onChange={(e) => onUpdate({ ...scene, broll: { ...scene.broll!, source: 'lottie', url: e.target.value } })} placeholder="Paste LottieFiles JSON URL..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                          <p className="text-xs text-gray-400">Auto-recolored to Antelope brand palette</p>
                        </>
                      )}

                      {/* Upload - just browse/paste */}
                      {brollSource === 'upload' && (
                        <div className="flex gap-2">
                          <button onClick={handleUpload} className="flex-1 py-2 border rounded-lg text-sm">📁 Browse</button>
                          <button onClick={handlePaste} className="flex-1 py-2 border rounded-lg text-sm">📋 Paste</button>
                        </div>
                      )}
                      
                      {/* Library - browse pre-approved assets */}
                      {brollSource === 'library' && (
                        <>
                          <div className="flex gap-2 mb-2">
                            {['all', 'clips', 'broll', 'infographics', 'overlays'].map(cat => (
                              <button
                                key={cat}
                                onClick={() => { setLibraryCategory(cat); setLibraryAssets([]); }}
                                className={`px-3 py-1 text-xs rounded-full ${libraryCategory === cat ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                              >
                                {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                              </button>
                            ))}
                          </div>
                          {loadingLibrary ? (
                            <div className="flex items-center justify-center py-4">
                              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                            </div>
                          ) : (
                            <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                              {ensureArray(libraryAssets).map((asset, i) => (
                                <button
                                  key={i}
                                  onClick={() => {
                                    setSelectedAssets([asset.url]);
                                    onUpdate({ ...scene, broll: {
                                      prompt: asset.filename,
                                      source: 'library',
                                      url: asset.mediaType === 'video' && asset.thumbnailUrl ? asset.url : asset.url,
                                      mediaType: asset.mediaType,
                                    } });
                                  }}
                                  className={`aspect-square rounded overflow-hidden border-2 relative ${selectedAssets.includes(asset.url) ? 'border-green-500' : 'border-gray-200'}`}
                                  title={`${asset.filename}${asset.duration ? ` (${asset.duration.toFixed(1)}s)` : ''}`}
                                >
                                  <img
                                    src={asset.thumbnailUrl || asset.url}
                                    className="w-full h-full object-cover"
                                    alt={asset.filename}
                                  />
                                  {asset.mediaType === 'video' && (
                                    <>
                                      <div className="absolute top-1 left-1 bg-black/70 text-white text-[9px] px-1 rounded flex items-center gap-0.5">
                                        <Play className="w-2 h-2" fill="currentColor" /> Video
                                      </div>
                                      {asset.duration && (
                                        <div className="absolute bottom-1 right-1 bg-black/70 text-white text-[9px] px-1 rounded">
                                          {asset.duration.toFixed(1)}s
                                        </div>
                                      )}
                                    </>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                          {ensureArray(libraryAssets).length === 0 && !loadingLibrary && (
                            <p className="text-xs text-gray-400 text-center py-2">No assets in this category</p>
                          )}
                        </>
                      )}
                      {ensureArray(searchResults).length > 0 && (
                        <div className="grid grid-cols-4 gap-2">
                          {ensureArray(searchResults).map((url, i) => (
                            <button key={i} onClick={() => setPreviewingUrl(url)} className={`aspect-square rounded overflow-hidden border-2 ${previewingUrl === url ? 'border-blue-500' : 'border-gray-200'}`}>
                              <img src={url} className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      )}
                      {previewingUrl && !selectedAssets.includes(previewingUrl) && (
                        <button onClick={async () => { await addSearchResultToAssets(previewingUrl); setPreviewingUrl(null); }} disabled={isUploading} className="w-full py-2 bg-green-500 text-white rounded-lg text-sm disabled:opacity-50">
                          {isUploading ? 'Uploading...' : '✓ Add to Selected'}
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            
            {/* Desktop: Side by side */}
            <div className="hidden md:grid md:grid-cols-2 gap-4">
              {/* Left - Controls */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Scene Type</label>
                  <select value={scene.type} onChange={(e) => handleTypeChange(e.target.value as SceneType)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                    {Object.entries(SCENE_TYPE_CONFIG).map(([type, cfg]) => <option key={type} value={type}>{cfg.icon} {cfg.label}</option>)}
                  </select>
                </div>

                {/* Effect & Sound */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Effect</label>
                    <select 
                      value={scene.effect || 'none'} 
                      onChange={(e) => { onUpdate({ ...scene, effect: e.target.value as EffectType }); replayAnimation(); }}
                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500"
                    >
                      {EFFECTS.map(eff => <option key={eff.id} value={eff.id}>{eff.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Sound</label>
                    <select 
                      value={scene.sound || 'none'} 
                      onChange={(e) => {
                        const newSound = e.target.value as SoundType;
                        onUpdate({ ...scene, sound: newSound });
                        playSound(newSound);
                      }}
                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500"
                    >
                      {SOUNDS.map(snd => <option key={snd.id} value={snd.id}>{snd.name}</option>)}
                    </select>
                  </div>
                </div>
                {/* Replay button */}
                <button 
                  onClick={replayAnimation}
                  className="w-full py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded flex items-center justify-center gap-1"
                >
                  ▶️ Preview Effect
                </button>

                {/* Caption Controls */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Caption Style</label>
                    <select 
                      value={scene.captionPreset || 'default'} 
                      onChange={(e) => onUpdate({ ...scene, captionPreset: e.target.value })}
                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500"
                    >
                      <option value="default">Default</option>
                      {appSettings?.captionPresets?.map(preset => (
                        <option key={preset.id} value={preset.id}>{preset.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Position</label>
                    <select 
                      value={scene.captionPositionOverride || ''} 
                      onChange={(e) => onUpdate({ ...scene, captionPositionOverride: e.target.value ? parseInt(e.target.value) as CaptionPositionNum : undefined })}
                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500"
                    >
                      <option value="">Default</option>
                      {POSITION_LABELS.map(pos => (
                        <option key={pos.value} value={pos.value}>{pos.value} - {pos.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {scene.type === 'OVERLAY' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Overlay Template</label>
                    <select value={scene.overlayTemplate || 'phone'} onChange={(e) => handleOverlayTemplateChange(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                      {overlayLibrary.map(o => <option key={o.id} value={o.id}>📺 {o.name}</option>)}
                    </select>
                  </div>
                )}
                
                {/* PIP Position Picker for CIRCLE_PIP - Desktop */}
                {scene.type === 'CIRCLE_PIP' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">PIP Position</label>
                    <div className="grid grid-cols-3 gap-1.5 max-w-[150px]">
                      {PIP_POSITIONS.map((pos) => (
                        <button
                          key={pos.id}
                          onClick={() => onUpdate({ ...scene, pipPosition: pos.id })}
                          className={`aspect-square rounded border-2 text-xs font-bold transition-all flex items-center justify-center ${
                            (scene.pipPosition || 'bottom-right') === pos.id 
                              ? 'border-green-500 bg-green-100 text-green-700' 
                              : 'border-gray-200 hover:border-gray-300 text-gray-400 hover:text-gray-600'
                          }`}
                        >
                          {pos.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {config.brollRequired && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">B-roll Source</label>
                      <select value={brollSource} onChange={(e) => setBrollSource(e.target.value as BrollSourceType)} className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs">
                        {BROLL_SOURCES.map(src => <option key={src.id} value={src.id}>{src.icon} {src.name}</option>)}
                      </select>
                    </div>
                    
                    {/* Flux/LoRA/Brave generate */}
                    {(brollSource === 'flux' || brollSource === 'lora' || brollSource === 'brave' || brollSource === 'pexels') && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">{brollSource === 'brave' || brollSource === 'pexels' ? 'Search' : 'Generate'}</label>
                        <div className="space-y-1">
                          <input type="text" value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={brollSource === 'brave' || brollSource === 'pexels' ? 'Search query...' : 'Generation prompt...'} className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs" />
                          <button onClick={handleGenerateOrSearch} disabled={isGenerating || !prompt} className="w-full px-2 py-1.5 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-1">
                            {isGenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} {brollSource === 'brave' || brollSource === 'pexels' ? 'Search' : 'Generate'}
                          </button>
                        </div>

                        {ensureArray(searchResults).length > 0 && (
                          <div className="mt-3">
                            <label className="block text-xs font-medium text-gray-500 mb-1.5">Results</label>
                            <div className="grid grid-cols-4 gap-2">
                              {ensureArray(searchResults).map((url, index) => (
                                <button key={index} onClick={() => setPreviewingUrl(url)} className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${previewingUrl === url ? 'border-blue-500 ring-2 ring-blue-200' : selectedAssets.includes(url) ? 'border-green-500' : 'border-gray-200 hover:border-gray-300'}`}>
                                  <img src={url} alt="" className="w-full h-full object-cover" />
                                  {selectedAssets.includes(url) && <div className="absolute top-1 right-1 p-1 bg-green-500 rounded-full"><Check className="w-3 h-3 text-white" /></div>}
                                  {previewingUrl === url && !selectedAssets.includes(url) && <div className="absolute top-1 right-1 p-1 bg-blue-500 rounded-full"><Eye className="w-3 h-3 text-white" /></div>}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Infographic options */}
                    {brollSource === 'infographic' && (
                      <div className="space-y-2">
                        <div className="flex gap-1">
                          <button onClick={handleUpload} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 border border-gray-200 rounded text-xs hover:bg-gray-50">
                            <Upload className="w-3 h-3" /> Upload PNG
                          </button>
                          <button onClick={handlePaste} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 border border-gray-200 rounded text-xs hover:bg-gray-50">
                            <Clipboard className="w-3 h-3" /> Paste
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-1">
                          <select value={scene.broll?.infographicDirection || 'bottom'} onChange={(e) => onUpdate({ ...scene, broll: { ...scene.broll!, infographicDirection: e.target.value as InfographicDirection } })} className="px-2 py-1.5 border rounded text-xs">
                            {INFOGRAPHIC_DIRECTIONS.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                          </select>
                          <select value={scene.broll?.infographicBgStyle || 'bokeh'} onChange={(e) => onUpdate({ ...scene, broll: { ...scene.broll!, infographicBgStyle: e.target.value as InfographicBgStyle } })} className="px-2 py-1.5 border rounded text-xs">
                            {INFOGRAPHIC_BG_STYLES.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </select>
                        </div>
                      </div>
                    )}
                    
                    {/* Component options */}
                    {brollSource === 'component' && (
                      <div className="space-y-2">
                        <select value={scene.broll?.componentType || 'metric-card'} onChange={(e) => onUpdate({ ...scene, broll: { ...scene.broll!, source: 'component', componentType: e.target.value as ComponentType } })} className="w-full px-2 py-1.5 border rounded text-xs">
                          {COMPONENT_TYPES.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                        </select>
                        <textarea 
                          value={componentNotes} 
                          onChange={(e) => { setComponentNotes(e.target.value); onUpdate({ ...scene, broll: { ...scene.broll!, componentNotes: e.target.value } }); }}
                          placeholder="Describe what you want..."
                          className="w-full px-2 py-1.5 border rounded text-xs h-16 resize-none"
                        />
                      </div>
                    )}
                    
                    {/* Lottie options (compact) */}
                    {brollSource === 'lottie' && (
                      <div className="space-y-1">
                        <input type="text" value={scene.broll?.url || ''} onChange={(e) => onUpdate({ ...scene, broll: { ...scene.broll!, source: 'lottie', url: e.target.value } })} placeholder="LottieFiles JSON URL..." className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs" />
                        <p className="text-[10px] text-gray-400">Auto-branded to Antelope colors</p>
                      </div>
                    )}

                    {/* Upload options */}
                    {brollSource === 'upload' && (
                      <div onDrop={handleDrop} onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }} onDragLeave={() => setIsDragging(false)} className={`border border-dashed rounded-lg p-2 ${isDragging ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                        <div className="flex gap-1">
                          <button onClick={handleUpload} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 border border-gray-200 rounded text-xs hover:bg-gray-50 bg-white">
                            <Upload className="w-3 h-3" /> Browse
                          </button>
                          <button onClick={handlePaste} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 border border-gray-200 rounded text-xs hover:bg-gray-50 bg-white">
                            <Clipboard className="w-3 h-3" /> Paste
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Library options (compact) */}
                    {brollSource === 'library' && (
                      <div className="space-y-2">
                        <div className="flex gap-1 flex-wrap">
                          {['all', 'clips', 'broll', 'infographics'].map(cat => (
                            <button
                              key={cat}
                              onClick={() => { setLibraryCategory(cat); setLibraryAssets([]); }}
                              className={`px-2 py-0.5 text-[10px] rounded-full ${libraryCategory === cat ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                            >
                              {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                            </button>
                          ))}
                        </div>
                        {loadingLibrary ? (
                          <Loader2 className="w-4 h-4 animate-spin text-gray-400 mx-auto" />
                        ) : (
                          <div className="grid grid-cols-3 gap-1 max-h-32 overflow-y-auto">
                            {ensureArray(libraryAssets).slice(0, 9).map((asset, i) => (
                              <button
                                key={i}
                                onClick={() => {
                                  setSelectedAssets([asset.url]);
                                  onUpdate({ ...scene, broll: {
                                    prompt: asset.filename,
                                    source: 'library',
                                    url: asset.url,
                                    mediaType: asset.mediaType,
                                  } });
                                }}
                                className={`aspect-square rounded overflow-hidden border relative ${selectedAssets.includes(asset.url) ? 'border-green-500' : 'border-gray-200'}`}
                              >
                                <img src={asset.thumbnailUrl || asset.url} className="w-full h-full object-cover" alt={asset.filename} />
                                {asset.mediaType === 'video' && (
                                  <div className="absolute top-0.5 left-0.5 bg-black/70 text-white text-[8px] px-1 rounded flex items-center gap-0.5">
                                    <Play className="w-2 h-2" fill="currentColor" />
                                  </div>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Right - Preview */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-medium text-gray-500">Preview {previewingUrl && !selectedAssets.includes(previewingUrl) && <span className="text-blue-500">(previewing)</span>}</label>
                  <div className="flex items-center gap-0.5 bg-gray-100 rounded p-0.5">
                    {(['S', 'M', 'L'] as const).map((size) => (
                      <button key={size} onClick={() => setPreviewSize(size)} className={`px-1.5 py-0.5 text-[10px] rounded transition-colors ${previewSize === size ? 'bg-white shadow text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`}>{size}</button>
                    ))}
                  </div>
                </div>
                <div className="mx-auto transition-all" style={{ maxWidth: previewSize === 'S' ? '120px' : previewSize === 'M' ? '200px' : '100%' }}>
                  <div className="relative group">
                    <ScenePreview
                      scene={scene}
                      avatarUrl={avatarUrl}
                      previewingUrl={previewingUrl}
                      captionSettings={captionSettings}
                      className="rounded-lg border border-gray-200"
                    />
                    
                    {(previewingUrl || scene.broll?.url) && scene.type !== 'SPEAKER_ONLY' && (
                      <button onClick={() => setLightboxUrl(previewingUrl || scene.broll?.url || null)} className="absolute top-2 right-2 p-1.5 bg-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70">
                        <Maximize2 className="w-4 h-4 text-white" />
                      </button>
                    )}
                  </div>
                  
                  {previewingUrl && !selectedAssets.includes(previewingUrl) && (
                    <button onClick={async () => { await addSearchResultToAssets(previewingUrl); setPreviewingUrl(null); }} disabled={isUploading} className="mt-2 w-full py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 disabled:opacity-50 flex items-center justify-center gap-2">
                      {isUploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</> : <><Check className="w-4 h-4" /> Add to Selected</>}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Selected Assets */}
            {config.brollRequired && ensureArray(selectedAssets).length > 0 && (
              <div className="mt-4">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Selected Assets</label>
                <div className="flex gap-2 flex-wrap">
                  {ensureArray(selectedAssets).map((url, index) => (
                    <div key={index} className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${scene.broll?.url === url ? 'border-green-500' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => selectAsset(url)}>
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <button onClick={(e) => { e.stopPropagation(); removeAsset(index); }} className="absolute top-0.5 right-0.5 p-0.5 bg-black/50 rounded-full hover:bg-black/70">
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ))}
                  <button onClick={handleUpload} className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-gray-400">
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxUrl && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setLightboxUrl(null)}>
          <div className="relative max-w-4xl max-h-[90vh] w-full flex flex-col items-center">
            <img src={lightboxUrl} alt="Full view" className="max-w-full max-h-[85vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
            <button onClick={() => setLightboxUrl(null)} className="absolute top-2 right-2 p-2 bg-black/50 rounded-full text-white/70 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
